import type { PortalLocale } from "./consultant-portal";

const en = {
  eyebrow: "Consultant portal",
  registerTitle: "Register as a consultant",
  registerIntro: "Create your account, confirm your e-mail and maintain your profile once OXORY has approved your registration.",
  loginTitle: "Consultant portal sign in",
  loginIntro: "Sign in to maintain your master data and availability.",
  forgotTitle: "Forgot your password?",
  forgotIntro: "Enter your e-mail and we will send you a link to set a new password.",
  resetTitle: "Set a new password",
  resetIntro: "Choose a new password with at least 10 characters.",
  pendingTitle: "Registration awaiting approval",
  profileTitle: "My profile",
  profileIntro: "Keep your master data and availability up to date. Only OXORY sees this information.",
  firstName: "First name",
  lastName: "Last name",
  email: "E-mail",
  password: "Password",
  passwordConfirm: "Confirm password",
  passwordHint: "At least 10 characters, including letters and numbers.",
  consentPre: "I have read the ",
  consentLink: "privacy policy",
  consentPost: " and agree that OXORY stores my registration data to manage my consultant profile and to place me in projects.",
  storedNotice: "We store your name, e-mail and the profile data you enter (address, contact, birth year, citizenship, languages, social profiles, availability and role) solely for registration and project placement — pre-contractual measures under Art. 6(1)(b) GDPR. You can delete your account and data at any time.",
  registerBtn: "Create account",
  loginBtn: "Sign in",
  sendLink: "Send reset link",
  savePassword: "Save new password",
  forgotLink: "Forgot password?",
  toRegister: "No account yet? Register",
  toLogin: "Already registered? Sign in",
  checkEmail: "Almost done: please check your inbox and click the confirmation link. After confirmation, OXORY reviews your registration.",
  resetSent: "If an account exists for this e-mail, a reset link is on its way.",
  resetDone: "Your password has been updated. You can now sign in.",
  resetNeedLink: "Open this page from the reset link in your e-mail.",
  pendingBody: "Your registration is awaiting approval by OXORY. We will let you know as soon as your access is activated.",
  rejectedBody: "Your registration was not approved. If you have questions, please contact info@oxory.net.",
  blockedBody: "Your portal access has been blocked. Please contact info@oxory.net.",
  noPortalAccount: "This account is not registered for the consultant portal.",
  signOut: "Sign out",
  save: "Save profile",
  saved: "Profile saved.",
  saveFailed: "Could not save. Please check your entries.",
  sections: {
    name: "Name",
    address: "Address",
    contact: "Contact",
    personal: "Personal",
    social: "Social profiles",
    availability: "Availability & role",
  },
  street: "Street and number",
  postalCode: "Postal code",
  city: "City",
  country: "Country (ISO code, e.g. DE)",
  phone: "Phone",
  emailReadonly: "E-mail (from registration, read-only)",
  birthYear: "Birth year",
  nationality: "Citizenship (ISO code, e.g. TR)",
  nativeLanguage: "Native language",
  foreignLanguages: "Foreign languages",
  language: "Language",
  level: "Level",
  addLanguage: "Add language",
  remove: "Remove",
  availabilityStatus: "Availability",
  availabilityOptions: {
    available: "Available now",
    available_from: "Available from a date",
    partially: "Partially available",
    not_available: "Not available",
  },
  earliestStart: "Earliest start date",
  availabilityNote: "Note on availability",
  role: "Consultant function",
  choose: "Please choose",
  deleteTitle: "Delete my account and data",
  deleteBody: "This permanently deletes your consultant profile and your login. This cannot be undone.",
  deleteConfirm: "Delete permanently",
  cancel: "Cancel",
  deleted: "Your account and data have been deleted.",
  errors: {
    required: "Required.",
    invalid_email: "Please enter a valid e-mail.",
    password_short: "At least 10 characters.",
    password_weak: "Use letters and numbers.",
    password_mismatch: "The passwords do not match.",
    consent_required: "Please accept the privacy notice.",
    https_only: "Use a full https:// link.",
    invalid_country: "Two-letter ISO code.",
    invalid_phone: "Invalid phone number.",
    birth_year_range: "Year between 1940 and the year you turned 18.",
    invalid_date: "Invalid date.",
    blocked: "Your registration could not be submitted. Please wait a moment and try again.",
    failed: "Registration failed. Please try again later.",
    login: "Sign-in failed. Check your e-mail and password, and confirm your e-mail first.",
    generic: "Something went wrong. Please try again.",
  } as Record<string, string>,
  ctaEyebrow: "Consultant portal",
  ctaTitle: "Consultant portal – register / log in",
  ctaBody: "Create your own account, confirm your e-mail and keep your master data and availability up to date after approval by OXORY.",
  ctaRegister: "Register",
  ctaLogin: "Log in",
};

export type PortalStrings = typeof en;

const tr: PortalStrings = {
  eyebrow: "Uzman portalı",
  registerTitle: "Uzman olarak kayıt olun",
  registerIntro: "Hesabınızı oluşturun, e-postanızı onaylayın; OXORY kaydınızı onayladıktan sonra profilinizi yönetin.",
  loginTitle: "Uzman portalı girişi",
  loginIntro: "Ana verilerinizi ve müsaitliğinizi güncellemek için giriş yapın.",
  forgotTitle: "Şifrenizi mi unuttunuz?",
  forgotIntro: "E-posta adresinizi girin; yeni şifre belirlemeniz için bir bağlantı gönderelim.",
  resetTitle: "Yeni şifre belirleyin",
  resetIntro: "En az 10 karakterden oluşan yeni bir şifre seçin.",
  pendingTitle: "Kaydınız onay bekliyor",
  profileTitle: "Profilim",
  profileIntro: "Ana verilerinizi ve müsaitliğinizi güncel tutun. Bu bilgileri yalnızca OXORY görür.",
  firstName: "Ad",
  lastName: "Soyad",
  email: "E-posta",
  password: "Şifre",
  passwordConfirm: "Şifre tekrarı",
  passwordHint: "Harf ve rakam içeren en az 10 karakter.",
  consentPre: "",
  consentLink: "Gizlilik politikasını",
  consentPost: " okudum; OXORY'nin uzman profilimi yönetmek ve beni projelere yerleştirmek amacıyla kayıt verilerimi saklamasını kabul ediyorum.",
  storedNotice: "Adınızı, e-postanızı ve girdiğiniz profil verilerini (adres, iletişim, doğum yılı, vatandaşlık, diller, sosyal profiller, müsaitlik ve rol) yalnızca kayıt ve proje yerleştirme amacıyla saklarız — GDPR md. 6(1)(b) kapsamında sözleşme öncesi tedbirler. Hesabınızı ve verilerinizi istediğiniz zaman silebilirsiniz.",
  registerBtn: "Hesap oluştur",
  loginBtn: "Giriş yap",
  sendLink: "Sıfırlama bağlantısı gönder",
  savePassword: "Yeni şifreyi kaydet",
  forgotLink: "Şifremi unuttum",
  toRegister: "Hesabınız yok mu? Kayıt olun",
  toLogin: "Zaten kayıtlı mısınız? Giriş yapın",
  checkEmail: "Neredeyse bitti: lütfen gelen kutunuzu kontrol edin ve onay bağlantısına tıklayın. Onaydan sonra OXORY kaydınızı inceler.",
  resetSent: "Bu e-posta için bir hesap varsa sıfırlama bağlantısı gönderildi.",
  resetDone: "Şifreniz güncellendi. Artık giriş yapabilirsiniz.",
  resetNeedLink: "Bu sayfayı e-postanızdaki sıfırlama bağlantısından açın.",
  pendingBody: "Kaydınız OXORY tarafından onay bekliyor. Erişiminiz etkinleştirildiğinde size haber vereceğiz.",
  rejectedBody: "Kaydınız onaylanmadı. Sorularınız için info@oxory.net adresine yazabilirsiniz.",
  blockedBody: "Portal erişiminiz engellendi. Lütfen info@oxory.net ile iletişime geçin.",
  noPortalAccount: "Bu hesap uzman portalına kayıtlı değil.",
  signOut: "Çıkış yap",
  save: "Profili kaydet",
  saved: "Profil kaydedildi.",
  saveFailed: "Kaydedilemedi. Lütfen girdilerinizi kontrol edin.",
  sections: {
    name: "Ad",
    address: "Adres",
    contact: "İletişim",
    personal: "Kişisel bilgiler",
    social: "Sosyal profiller",
    availability: "Müsaitlik ve rol",
  },
  street: "Sokak ve numara",
  postalCode: "Posta kodu",
  city: "Şehir",
  country: "Ülke (ISO kodu, örn. TR)",
  phone: "Telefon",
  emailReadonly: "E-posta (kayıttan, değiştirilemez)",
  birthYear: "Doğum yılı",
  nationality: "Vatandaşlık (ISO kodu, örn. TR)",
  nativeLanguage: "Ana dil",
  foreignLanguages: "Yabancı diller",
  language: "Dil",
  level: "Seviye",
  addLanguage: "Dil ekle",
  remove: "Kaldır",
  availabilityStatus: "Müsaitlik",
  availabilityOptions: {
    available: "Hemen müsait",
    available_from: "Belirli bir tarihten itibaren",
    partially: "Kısmen müsait",
    not_available: "Müsait değil",
  },
  earliestStart: "En erken başlangıç tarihi",
  availabilityNote: "Müsaitlik notu",
  role: "Danışman rolü",
  choose: "Lütfen seçin",
  deleteTitle: "Hesabımı ve verilerimi sil",
  deleteBody: "Bu işlem uzman profilinizi ve girişinizi kalıcı olarak siler. Geri alınamaz.",
  deleteConfirm: "Kalıcı olarak sil",
  cancel: "Vazgeç",
  deleted: "Hesabınız ve verileriniz silindi.",
  errors: {
    required: "Zorunlu alan.",
    invalid_email: "Geçerli bir e-posta girin.",
    password_short: "En az 10 karakter.",
    password_weak: "Harf ve rakam kullanın.",
    password_mismatch: "Şifreler eşleşmiyor.",
    consent_required: "Lütfen gizlilik bildirimini kabul edin.",
    https_only: "Tam bir https:// bağlantısı kullanın.",
    invalid_country: "İki harfli ISO kodu.",
    invalid_phone: "Geçersiz telefon numarası.",
    birth_year_range: "1940 ile 18 yaşınızı doldurduğunuz yıl arasında.",
    invalid_date: "Geçersiz tarih.",
    blocked: "Kaydınız gönderilemedi. Lütfen biraz bekleyip tekrar deneyin.",
    failed: "Kayıt başarısız oldu. Lütfen daha sonra tekrar deneyin.",
    login: "Giriş başarısız. E-posta ve şifrenizi kontrol edin; önce e-postanızı onaylayın.",
    generic: "Bir hata oluştu. Lütfen tekrar deneyin.",
  },
  ctaEyebrow: "Uzman portalı",
  ctaTitle: "Uzman portalı – kayıt / giriş",
  ctaBody: "Kendi hesabınızı oluşturun, e-postanızı onaylayın ve OXORY onayından sonra ana verilerinizi ve müsaitliğinizi güncel tutun.",
  ctaRegister: "Kayıt ol",
  ctaLogin: "Giriş yap",
};

export const PORTAL_STRINGS: Record<PortalLocale, PortalStrings> = { en, tr };

export function portalMeta(title: string, description: string) {
  return {
    meta: [
      { title: `${title} | OXORY` },
      { name: "description", content: description },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: `${title} | OXORY` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  };
}
